/**
 * NAV-5.0 / NAV-5.1 / NAV-5.3 — who sees the Commission area, and what it contains.
 *
 * The Commission group is
 * `commissionEnabled && (commission.view || cost.view || system.override)`, so
 * it is absent on an account that has not been enabled.
 *
 * ⚠️ THERE IS NO ENABLE SWITCH IN THIS APP. Since LDG-1.90 (2026-10-02) the
 * account is turned on only from the admin portal (ADM-1.3), because commission
 * is a paid add-on and enabling it is provisioning. The superadmin-only
 * Settings → Commission access leaf that NAV-5.0 kept for it is gone.
 *
 * ⚠️ THIS FILE DECIDES NOTHING THE DATABASE DOES NOT ALSO DECIDE. Each
 * destination's card still asks the database. What lives here is the same
 * rule, so the sidebar and a hand-typed URL cannot disagree: a visible entry
 * that leads to an empty payroll page is how this product lies.
 *
 * ⚠️ `cost.view` IS WIDER THAN `commission.view`. Internal PM and Internal PM
 * Manager hold it. They see the group for Setup → Rate cards, with every money
 * destination absent. That is intended.
 */

export const SYSTEM_OVERRIDE = 'system.override';
export const COMMISSION_VIEW = 'commission.view';
export const COST_VIEW = 'cost.view';
export const MANAGE_SETTINGS = 'manage_settings';

export const COMMISSION_OVERVIEW_HREF = '/app/commission';
export const COMMISSION_SETUP_HREF = '/app/commission/setup';
export const COMMISSION_SETUP_DEFAULT_SECTION = 'plans';

export const COMMISSION_AREA_COPY = {
  noDestination:
    'You cannot see this page. That is not the same as there being nothing here.',
  groupLabel: 'Commission',
} as const;

export type CommissionNavViewer = {
  permissionsLoading: boolean;
  /** Enablement fetch from the same `useCommissionCostingEnabled` call. */
  commissionLoading: boolean;
  commissionEnabled: boolean;
  hasPermission: (permission: string) => boolean;
};

/** Unresolved gates are not a deny. Callers skeleton (or stay absent) first. */
export function commissionNavGatesUnresolved(viewer: CommissionNavViewer): boolean {
  return viewer.permissionsLoading || viewer.commissionLoading;
}

/**
 * One gate on the Commission group. Each destination keeps its own permission
 * inside; this only answers whether the labelled group renders at all.
 */
export function canSeeCommissionGroup(viewer: CommissionNavViewer): boolean {
  if (viewer.permissionsLoading || !viewer.commissionEnabled) return false;
  return (
    viewer.hasPermission(COMMISSION_VIEW) ||
    viewer.hasPermission(COST_VIEW) ||
    viewer.hasPermission(SYSTEM_OVERRIDE)
  );
}

export const COMMISSION_DESTINATIONS = [
  { id: 'overview', title: 'Overview', href: '/app/commission', kind: 'money' },
  { id: 'periods', title: 'Pay periods', href: '/app/commission/periods', kind: 'money' },
  { id: 'money-held', title: 'Money held', href: '/app/commission/money-held', kind: 'money' },
  { id: 'draws', title: 'Draws', href: '/app/commission/draws', kind: 'money' },
  { id: 'guarantees', title: 'Guarantees', href: '/app/commission/guarantees', kind: 'money' },
  { id: 'cancelled', title: 'Cancelled deals', href: '/app/commission/cancelled', kind: 'money' },
  { id: 'setup', title: 'Setup', href: '/app/commission/setup', kind: 'setup' },
] as const;

export type CommissionDestinationId = (typeof COMMISSION_DESTINATIONS)[number]['id'];
export type CommissionDestination = (typeof COMMISSION_DESTINATIONS)[number];

function destinationById(id: CommissionDestinationId): CommissionDestination {
  const found = COMMISSION_DESTINATIONS.find((d) => d.id === id);
  if (!found) throw new Error(`Unknown commission destination: ${id}`);
  return found;
}

/**
 * Money destinations need `commission.view` (or override). Setup needs
 * `cost.view` or `manage_settings` (or override), so Internal PM reaches
 * Rate cards without seeing payroll.
 */
export function canSeeCommissionDestination(
  id: CommissionDestinationId,
  viewer: CommissionNavViewer,
): boolean {
  if (!canSeeCommissionGroup(viewer)) return false;
  const dest = destinationById(id);
  if (dest.kind === 'money') {
    return viewer.hasPermission(COMMISSION_VIEW) || viewer.hasPermission(SYSTEM_OVERRIDE);
  }
  return (
    viewer.hasPermission(COST_VIEW) ||
    viewer.hasPermission(MANAGE_SETTINGS) ||
    viewer.hasPermission(SYSTEM_OVERRIDE)
  );
}

export function visibleCommissionDestinations(
  viewer: CommissionNavViewer,
): CommissionDestination[] {
  return COMMISSION_DESTINATIONS.filter((d) => canSeeCommissionDestination(d.id, viewer));
}

export const COMMISSION_SETUP_GROUPS = ['The rules', 'People', 'Cost & margin'] as const;
export type CommissionSetupGroup = (typeof COMMISSION_SETUP_GROUPS)[number];

export const COMMISSION_SETUP_SECTIONS = [
  { id: 'plans', title: 'Plans', group: 'The rules', kind: 'configure' },
  // COM-10.1. Beside Plans because a product is what a redline varies BY, and
  // the plan editor's "Product this redline applies to" picker reads this list.
  { id: 'products', title: 'Products', group: 'The rules', kind: 'configure' },
  { id: 'cadence', title: 'Pay cadence', group: 'The rules', kind: 'configure' },
  { id: 'agreements', title: 'Agreements', group: 'The rules', kind: 'configure' },
  { id: 'payroll', title: 'Payroll references', group: 'People', kind: 'configure' },
  // OVR-1.3. Who earns the override on each rep's deals.
  { id: 'managers', title: 'Override managers', group: 'People', kind: 'configure' },
  // OVR-3.4. Reps in training and who is training them.
  { id: 'training', title: 'Reps in training', group: 'People', kind: 'configure' },
  { id: 'derivation', title: 'Derivation', group: 'Cost & margin', kind: 'cost' },
  { id: 'rate-cards', title: 'Rate cards', group: 'Cost & margin', kind: 'cost' },
] as const;

export type CommissionSetupSectionId = (typeof COMMISSION_SETUP_SECTIONS)[number]['id'];
export type CommissionSetupSection = (typeof COMMISSION_SETUP_SECTIONS)[number];

export function isCommissionSetupSectionId(value: string | undefined): value is CommissionSetupSectionId {
  return COMMISSION_SETUP_SECTIONS.some((s) => s.id === value);
}

export function canSeeSetupSection(
  id: CommissionSetupSectionId,
  viewer: CommissionNavViewer,
): boolean {
  if (!canSeeCommissionDestination('setup', viewer)) return false;
  const section = COMMISSION_SETUP_SECTIONS.find((s) => s.id === id);
  if (!section) return false;
  if (section.kind === 'cost') {
    return viewer.hasPermission(COST_VIEW) || viewer.hasPermission(SYSTEM_OVERRIDE);
  }
  return viewer.hasPermission(MANAGE_SETTINGS) || viewer.hasPermission(SYSTEM_OVERRIDE);
}

export function visibleSetupSections(viewer: CommissionNavViewer): CommissionSetupSection[] {
  return COMMISSION_SETUP_SECTIONS.filter((s) => canSeeSetupSection(s.id, viewer));
}

/** Overview is `/app/commission` and must not light up for `/app/commission/periods`. */
export function isCommissionOverviewPath(pathname: string): boolean {
  return pathname === '/app/commission';
}

export function isCommissionSetupPath(pathname: string): boolean {
  return pathname === '/app/commission/setup' || pathname.startsWith('/app/commission/setup/');
}

export function isCommissionAreaPath(pathname: string): boolean {
  return pathname === '/app/commission' || pathname.startsWith('/app/commission/');
}
